import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useAuth0 } from '../../hooks/useAuthPatched';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography } from '@mui/material';

const SessionWarning = () => {
    const { isAuthenticated, getIdTokenClaims, getAccessTokenSilently, logout } = useAuth0();
    const [showWarning, setShowWarning] = useState(false);
    const [timeRemaining, setTimeRemaining] = useState(null);
    const countdownIntervalRef = useRef(null);
    const warningTimerRef = useRef(null);
    const logoutTimerRef = useRef(null);

    const clearTimers = useCallback(() => {
        if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
        if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
        if (logoutTimerRef.current) clearTimeout(logoutTimerRef.current);
    }, []);

    const scheduleLogout = useCallback((timeUntilExpiryMs) => {
        if (logoutTimerRef.current) clearTimeout(logoutTimerRef.current);
        logoutTimerRef.current = setTimeout(() => {
            logout({ returnTo: window.location.origin });
        }, timeUntilExpiryMs);
    }, [logout]);

    const startCountdown = useCallback((timeUntilExpiryMs) => {
        if (countdownIntervalRef.current) {
            clearInterval(countdownIntervalRef.current);
        }
        setTimeRemaining(Math.floor(timeUntilExpiryMs / 1000));
        setShowWarning(true);

        countdownIntervalRef.current = setInterval(() => {
            setTimeRemaining(prev => {
                const next = (prev ?? Math.ceil(timeUntilExpiryMs / 1000)) - 1;
                if (next <= 0) {
                    clearInterval(countdownIntervalRef.current);
                    logout({ returnTo: window.location.origin });
                    return 0;
                }
                return next;
            });
        }, 1000);
    }, [logout]);

    const resetSessionTimers = useCallback(async () => {
        if (!isAuthenticated) {
            return;
        }
        const claims = await getIdTokenClaims().catch(() => null);
        const expiryTime = claims?.exp ? claims.exp * 1000 : null; // ms
        if (!expiryTime) {
            return;
        }
        const timeUntilExpiry = expiryTime - Date.now();
        if (timeUntilExpiry <= 0) {
            logout({ returnTo: window.location.origin });
            return;
        }

        const warningThreshold = 5 * 60 * 1000; // 5 minutes
        clearTimers();
        scheduleLogout(timeUntilExpiry);

        if (timeUntilExpiry <= warningThreshold) {
            startCountdown(timeUntilExpiry);
        } else {
            setShowWarning(false);
            setTimeRemaining(null);
        }

        warningTimerRef.current = setTimeout(resetSessionTimers, 60000);
    }, [clearTimers, getIdTokenClaims, isAuthenticated, logout, scheduleLogout, startCountdown]);

    useEffect(() => {
        resetSessionTimers();
        return () => {
            clearTimers();
        };
    }, [clearTimers, resetSessionTimers]);

    const handleRefresh = async () => {
        try {
            await getAccessTokenSilently({ cacheMode: 'off' }).catch(() => getAccessTokenSilently());
            await resetSessionTimers();
        } catch (e) {
            // If refresh fails, force logout
            logout({ returnTo: window.location.origin });
        }
    };

    const formatTime = (seconds) => {
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = seconds % 60;
        return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
    };

    return (
        <Dialog open={showWarning} onClose={() => setShowWarning(false)}>
            <DialogTitle>Session Expiring Soon</DialogTitle>
            <DialogContent>
                <Typography>
                    Your session will expire in {formatTime(timeRemaining)}. Would you like to refresh your session?
                </Typography>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setShowWarning(false)} color="secondary">
                    Dismiss
                </Button>
                <Button onClick={handleRefresh} color="primary" variant="contained">
                    Refresh Session
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default SessionWarning;
